import { Capacitor } from "@capacitor/core";

let registered = false;
let currentToken = null;
let registrationListener = null;
let registrationErrorListener = null;

/**
 * Register for push notifications on native platforms.
 * Call this after the user has logged in.
 */
export async function registerPush() {
  if (!Capacitor.isNativePlatform() || registered) return;

  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");

    // Check / request permissions
    let permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === "prompt") {
      permStatus = await PushNotifications.requestPermissions();
    }
    if (permStatus.receive !== "granted") {
      console.log("[Push] Permission denied");
      return;
    }

    // Listen for registration success
    registrationListener = PushNotifications.addListener("registration", async (token) => {
      console.log("[Push] Token:", token.value);
      registered = true;
      currentToken = token.value;

      // Send token to backend
      try {
        const axios = (await import("axios")).default;
        await axios.post("/api/v1/user/device-token", {
          token: token.value,
          platform: Capacitor.getPlatform(), // "ios" or "android"
        });
      } catch (err) {
        console.error("[Push] Failed to send token to server:", err);
      }
    });

    registrationErrorListener = PushNotifications.addListener("registrationError", (err) => {
      console.error("[Push] Registration error:", err.error);
    });

    // Create Android notification channel
    if (Capacitor.getPlatform() === "android") {
      PushNotifications.createChannel({
        id: "ride-updates",
        name: "Ride Updates",
        description: "Notifications for ride bookings, messages, and status changes",
        importance: 4,
        vibration: true,
        sound: "default",
      }).catch(() => {});
    }

    // Register with APNs / FCM
    await PushNotifications.register();
  } catch (err) {
    console.error("[Push] Setup error:", err);
  }
}

/**
 * Set up listener for notification taps (app was in background).
 * @param {Function} navigate - React Router navigate function
 */
export async function setupPushTapHandler(navigate) {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");

    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const data = action.notification.data;
      if (data?.rideId) {
        navigate(`/ride/${data.rideId}`);
      }
    });
  } catch (err) {
    console.error("[Push] Tap handler error:", err);
  }
}

/**
 * Get the current device push token.
 */
export function getCurrentToken() {
  return currentToken;
}

/**
 * Unregister push token on logout.
 */
export async function unregisterPush() {
  if (!Capacitor.isNativePlatform() || !registered) return;

  try {
    // Remove token from backend
    if (currentToken) {
      try {
        const axios = (await import("axios")).default;
        await axios.delete("/api/v1/user/device-token", {
          data: { token: currentToken },
        });
      } catch (err) {
        console.error("[Push] Failed to remove token from server:", err);
      }
    }

    // Remove Capacitor listeners
    if (registrationListener) {
      (await registrationListener).remove();
      registrationListener = null;
    }
    if (registrationErrorListener) {
      (await registrationErrorListener).remove();
      registrationErrorListener = null;
    }

    registered = false;
    currentToken = null;
  } catch (err) {
    console.error("[Push] Unregister error:", err);
  }
}
